import { useState, useEffect, useMemo } from 'react';

// javascript/style-guide/config.js
export const breakpoints = {
  xs: 320,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1360
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

const useResizeListener = (onResize) => {
  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);
};

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useResizeListener(() => setWindowDimensions(getWindowDimensions()));

  return windowDimensions;
}

export function useResponsiveChecks() {
  const { width } = useWindowDimensions();

  const isMobile = useMemo(() => width < breakpoints.sm, [width]);
  const isTabletVert = useMemo(() => width >= breakpoints.sm && width < breakpoints.md, [width]);
  const isTabletHoriz = useMemo(() => width >= breakpoints.md && width < breakpoints.lg, [width]);
  const isLaptopSmall = useMemo(() => width >= breakpoints.lg && width < breakpoints.xl, [width]);

  return {
    isMobile, isTabletVert, isTabletHoriz, isLaptopSmall
  };
}

export const useContainerDimensions = (myRef) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      if (!myRef.current) {
        setDimensions(prev => (prev.width === 0 && prev.height === 0 ? prev : { width: 0, height: 0 }));
        return;
      }

      const next = {
        width: myRef.current.offsetWidth,
        height: myRef.current.offsetHeight
      };

      setDimensions(prev => (
        prev.width === next.width && prev.height === next.height
          ? prev
          : next
      ));
    };

    if (myRef.current) {
      updateDimensions();
    }

    let observer;
    if (typeof ResizeObserver !== 'undefined' && myRef.current) {
      observer = new ResizeObserver(() => {
        updateDimensions();
      });
      observer.observe(myRef.current);
    }

    window.addEventListener('resize', updateDimensions);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, [myRef]);

  return dimensions;
};
